import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EnableSourcingChannelsRequest,
  SourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { queryKeys } from '@/query-keys';
import { JOVEO } from '@/types/channels';

import { ActionButton } from './action-button';
import { StatusPill } from './status-pill';

interface IntegratedChannelsTableRowProps {
  channel: SourcingChannelDetails;
}

export function IntegratedChannelsTableRow({
  channel,
}: IntegratedChannelsTableRowProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToaster();

  /* Mutation to activate channel */
  const { mutate: activateChannel, isPending: isActivatingChannel } =
    useMutation({
      mutationFn: async (requestBody: EnableSourcingChannelsRequest) => {
        const data =
          await SourcingService.postInternalApiSourcingSourcingChannelsEnableChannels(
            requestBody,
          );

        // The enable_channels API returns the channels that were activated, so if the channel is not in the list, it means it failed to activate
        if (
          !data.channels.some(
            channel => channel.slug === requestBody.channel_slugs?.[0],
          )
        ) {
          throw new Error('Failed to activate channel');
        }

        return data;
      },
      onSuccess: async () => {
        // Invalidate query to update the UI
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.settings.channels.integrated],
        });

        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Channel activated successfully',
            description: 'Success message for successful channel activation',
          }),
        });
      },
      onError: () => {
        notifyError({
          message: intl.formatMessage({
            defaultMessage: 'Failed to activate channel',
            description: 'Error message for failed channel activation',
          }),
        });
      },
    });

  /* Mutation to deactivate channel */
  const { mutate: deactivateChannel, isPending: isDeactivatingChannel } =
    useMutation({
      mutationFn: (channelId: string) =>
        SourcingService.deleteInternalApiSourcingSourcingChannels(channelId),
      onSuccess: async () => {
        // Invalidate query to update the UI
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.settings.channels.integrated],
        });

        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Channel deactivated successfully',
            description: 'Success message for successful channel deactivation',
          }),
        });
      },
      onError: () => {
        notifyError({
          message: intl.formatMessage({
            defaultMessage: 'Failed to deactivate channel',
            description: 'Error message for failed channel deactivation',
          }),
        });
      },
    });

  const handleActivateChannel = (channel: SourcingChannelDetails) => {
    activateChannel({ channel_slugs: [channel.slug] });
  };

  const handleDeactivateChannel = (channel: SourcingChannelDetails) => {
    deactivateChannel(channel.external_id);
  };

  return (
    <>
      <div
        className="grid items-center gap-x-4 px-4 py-3"
        style={{ gridTemplateColumns: '1fr 1fr 2fr' }}
      >
        {/* Channel name */}
        <ChannelIcon
          channelSlug={channel.slug}
          channelName={channel.name}
          variant="large"
          size="sm"
        />

        {/* Status */}
        <StatusPill isConnected={!!channel.is_added_to_account} />

        {/* Actions */}
        <div className="flex items-center">
          <ActionButton
            activateChannel={() => handleActivateChannel(channel)}
            deactivateChannel={() => handleDeactivateChannel(channel)}
            isActivated={!!channel.is_added_to_account}
            isActivating={isActivatingChannel}
            isDeactivating={isDeactivatingChannel}
            isJoveo={channel.slug === JOVEO}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="bg-fx-neutral-200 h-px w-full" />
    </>
  );
}

IntegratedChannelsTableRow.Skeleton =
  function IntegratedChannelsTableRowSkeleton() {
    return (
      <>
        <div
          className="h-15 grid items-center gap-x-4 px-4 py-3"
          style={{ gridTemplateColumns: '1fr 1fr 2fr' }}
        >
          {/* Channel name */}
          <div className="flex items-center gap-1">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-24" />
          </div>

          {/* Status */}
          <Skeleton className="h-6 w-28" />

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Skeleton className="h-4 w-24" />
          </div>
        </div>

        <div className="bg-fx-neutral-200 h-px w-full" />
      </>
    );
  };
